import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import { PAUSED_AB_TEST_KEYS } from 'nove8/constants/ab-tests'
import {
  NOT_UPSELL_LOCALIZATIONS,
  PaymentSystem,
} from 'nove8/constants/payment'
import { getGrowthbookVariantData } from 'nove8/helpers/getGrowthbookVariantData'
import { TStep } from 'nove8/types'
import { GrowthbookKey, LOCALES, TestGroup } from 'root-constants'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

const isEnLocale = () => {
  const langFromUrl = new URL(window.location.href).searchParams.get('lang')

  return (
    (langFromUrl || useZustandSessionStore.getState().language) === LOCALES.EN
  )
}

const isCommitmentScreen = () => {
  if (
    PAUSED_AB_TEST_KEYS.includes(
      GrowthbookKey.ABTEST_WEB_2867_COMMITMENT_SCREEN3,
    )
  ) {
    return false
  }

  const growthbook = useZustandGlobalStore.getState().growthbook
  if (!growthbook) return false

  return (
    isEnLocale() &&
    getGrowthbookVariantData({
      growthbook,
      growthbookKey: GrowthbookKey.ABTEST_WEB_2867_COMMITMENT_SCREEN3,
    }) === TestGroup.ABTEST_WEB_2867_B_COMMITMENT_SCREEN
  )
}

export const getXStateConfigWoofzPppBl = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    always: [
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return (
            !getHasEmail() &&
            !getHasSubscription() &&
            !getHasAccount() &&
            getHasAnswers() &&
            pageIdFromUrl === PageId.NEWSLETTER_URGE_WOOFZ_PPP_BL
          )
        },
        target: PageId.EMAIL_WOOFZ_PPP_BL,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          return !hasEmail && !hasSubscription && !hasAccount && hasAnswers
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageId.DOWNLOAD_WOOFZ_PPP_BL,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageId.ACCOUNT_NOVE8,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageId.SUBSCRIPTIONS_WOOFZ_PPP_BL,
      },
      {
        target: pages[0].pageId,
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.PREPARE_FLOW_WOOFZ_PPP_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: isCommitmentScreen,
                target: PageId.COMMITMENT_SCREEN_PPP_BL,
              },
              {
                target: PageId.EMAIL_WOOFZ_PPP_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.EMAIL_WOOFZ_PPP_BL && {
            PREVIOUS_FLOW_STEP: [
              {
                target: PageId.URGE_5_WOOFZ_PPP_BL,
              },
            ],
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return languageFromUrl !== 'en'
                },
                target: PageId.LAST_CHANCE_OFFER_WOOFZ_PPP_BL,
              },
              {
                target: pages[index + 1]?.pageId,
              },
            ],
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_WOOFZ_PPP_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_WOOFZ_PPP_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.CHAT_UPSELL_WOOFZ_PPP_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return NOT_UPSELL_LOCALIZATIONS.includes(
                    languageFromUrl as string,
                  )
                },
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.UPSELL_WOOFZ_PPP_BL,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_WOOFZ_PPP_BL && {
            NEXT_FLOW_STEP: [
              {
                cond: isEnLocale,
                target: PageId.UPSELL_FEEDING_GUIDE_WOOFZ_PPP_BL,
              },
              {
                target: PageId.ACCOUNT_NOVE8,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_FEEDING_GUIDE_WOOFZ_PPP_BL && {
            NEXT_FLOW_STEP: PageId.UPSELL_EMOTIONAL_SUPPORT_WOOFZ_PPP_BL,
          }),
          ...(page.pageId === PageId.UPSELL_EMOTIONAL_SUPPORT_WOOFZ_PPP_BL && {
            NEXT_FLOW_STEP: PageId.ACCOUNT_NOVE8,
          }),
        },
      },
    }),
    {},
  ),
  [PageId.ACCOUNT_NOVE8]: {
    on: {
      NEXT_FLOW_STEP: PageId.DOWNLOAD_WOOFZ_PPP_BL,
    },
  },
  [PageId.DOWNLOAD_WOOFZ_PPP_BL]: {
    type: 'final',
  },
})
